import { z } from 'zod'

import { Options, getOptionValue, getOptionLabel } from './options'

export const preprocessOptions = <T extends z.ZodTypeAny>(getOption: () => Options[], schema: T) =>
  z.preprocess(val => {
    if (val === '') return undefined

    if (typeof val === 'string') {
      const opts = getOption()
      return getOptionValue(opts, val)
    }
    throw new Error('Invalid type, expected string')
  }, schema) // as unknown as T

export const preprocessValue = <T extends z.ZodTypeAny>(getOptions: () => Options[], schema: T) =>
  z.preprocess(val => {
    if (val === '') return undefined

    if (typeof val === 'string' || typeof val === 'number') {
      const opts = getOptions()
      return getOptionLabel(opts, String(val))
    }
    return ''
  }, schema) // as unknown as T

export const preprocessId = () =>
  z.preprocess(val => {
    if (val === null || val === undefined || val === 'new') {
      return -1
    }
    if (typeof val === 'number' || !Number.isNaN(Number(val))) {
      return val
    }

    throw new Error('Invalid type, expected number or "new"')
  }, z.coerce.number()) // as unknown as z.ZodNumber

export const preprocessNumberToString = () =>
  z.preprocess(val => {
    if (val === null || val === undefined) {
      return null
    }
    if (typeof val === 'number' || !Number.isNaN(Number(val))) {
      return String(val)
    }

    throw new Error('Invalid type, expected number')
  }, z.string().nullish()) // as unknown as z.ZodNumber

export const preprocessSwitch = () =>
  z.preprocess(val => {
    if (val === 'true' || val === true || val === 1 || val === 'on') {
      return 'on'
    }
    return false
  }, z.boolean()) // as unknown as z.ZodBoolean

export const preprocessBoolean = () =>
  z.preprocess(val => {
    if (val === 'true' || val === true || val === 1 || val === 'on') {
      return true
    }
    return false
  }, z.boolean()) // as unknown as z.ZodBoolean

export const preprocessMultipleOptions = <T extends z.ZodTypeAny>(
  getOption: () => Options[],
  schema: T,
  delimiter = ';'
) =>
  z.preprocess(val => {
    if (typeof val === 'string') {
      const opts = getOption()
      const arr = val.split(delimiter).map(label => getOptionValue(opts, label))
      return arr
    }
    throw new Error('Invalid type, expected string')
  }, schema) // as unknown as T

export const preprocessArrayToString = <T extends z.ZodTypeAny>(
  getOption: () => Options[],
  schema: T,
  delimiter = ';'
) =>
  z.preprocess(val => {
    if (Array.isArray(val)) {
      const opts = getOption()
      const arr = (val as string[]).map(el => getOptionLabel(opts, String(el))).join(delimiter)
      return arr
    }
    throw new Error('Invalid type, expected array')
  }, schema) // as unknown as T

export const preprocessJSON = () => z.preprocess(val => JSON.stringify(val), z.any()) // as unknown as z.ZodString
